<script setup lang="ts">
import { computed } from 'vue';
import { DividerComponent } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import CalChoiceToggle from '@/application/smallBusiness/infrastructure/components/CalChoiceToggle.vue';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';

interface MockPlan {
  carrierId: string;
  name: string;
  detail: string;
}

interface MockTier {
  name: string;
  plans: Array<MockPlan>;
}

const applicationType = APPLICATION_TYPES.SMALL_BUSINESS;

const { smallBusinessState } = useSmallBusinessStore();

const zipCode = computed(() => {
  return smallBusinessState.value.application.company.main_address?.zip_code || '95492';
});

const employeesCount = computed(() => {
  return smallBusinessState.value.companySize || 5;
});

const contributionFilters = [
  { label: 'For Employees', value: '50%' },
  { label: 'For Dependents', value: '0%' }
];

const checkBoxFilters = [
  { name: 'Carriers', options: ['Kaiser', 'Blue Shield', 'Sharp', 'Western', 'United', 'Sutter'] },
  { name: 'Metal Tier', options: ['Bronze', 'Silver', 'Gold', 'Platinum'] },
  { name: 'Plan Type', options: ['HMO', 'PPO'] }
];

const tiers: Array<MockTier> = [{
  name: 'Bronze',
  plans: [
    { carrierId: 'kaiser', name: 'Bronze 60 HMO', detail: 'HSA $6,650/0%' },
    { carrierId: 'blue_shield', name: 'Bronze 60 Trio HMO', detail: '$7,000/$70' },
    { carrierId: 'sharp', name: 'Bronze 60 Premier HMO', detail: 'HSA $6,650/0%' },
    { carrierId: 'western', name: 'Bronze 60 HMO B', detail: '$5,800/$60' }
  ]
}, {
  name: 'Silver',
  plans: [
    { carrierId: 'kaiser', name: 'Silver 70 HMO', detail: 'HSA $2,850/25%' },
    { carrierId: 'blue_shield', name: 'Silver 70 Trio HMO', detail: '$2,500/$55' },
    { carrierId: 'united', name: 'Silver 70 HMO A', detail: '$2,750/$60' },
    { carrierId: 'sutter', name: 'Silver 70 HMO B', detail: '$2,500/$55' }
  ]
}];
</script>
<template>
  <div class="pointer-events-none select-none" aria-hidden="true">
    <div class="flex w-full lg:justify-center flex-col lg:flex-row mt-6 px-3 sm:px-6">
      <div class="hidden xl:flex flex-col xl:w-64 xl:pr-4 xl:shrink-0">
        <div class="flex w-full flex-col space-y-2 text-normal">
          <div class="flex-row">
            <hfc-icon icon="location-dot" class="text-blue-600" />
            <span class="ml-2">{{ zipCode }}</span>
          </div>
          <div class="flex-row">
            <hfc-icon icon="user" class="text-blue-600" />
            <span class="ml-2">{{ employeesCount }} Employees</span>
          </div>
        </div>
        <DividerComponent />
        <div class="flex w-48 flex-col justify-between space-y-3">
          <div class="flex">
            <div>Employer Contribution</div>
          </div>
          <div v-for="contribution in contributionFilters" :key="contribution.label" class="flex flex-row items-center justify-between">
            <div class="text-sm mr-2 whitespace-nowrap">{{ contribution.label }}</div>
            <div class="flex w-20 min-h-[40px] items-center justify-between rounded-md border-2 border-[#e9f0f6] bg-white px-2 text-sm">
              <span>{{ contribution.value }}</span>
              <span class="border-x-[5px] border-t-[5px] border-x-transparent border-t-[#999]"></span>
            </div>
          </div>
        </div>
        <DividerComponent />
        <div class="flex flex-col w-full max-w-xs space-y-4">
          <div v-for="filter in checkBoxFilters" :key="filter.name" class="flex flex-col max-w-xs">
            <div class="flex mb-1 font-semibold text-gray-700">{{ filter.name }}</div>
            <div v-for="option in filter.options" :key="option" class="flex w-full items-center space-x-2">
              <div>
                <input type="checkbox" class="flex border-gray-300 rounded-md w-4 h-4" :id="`mock-checkbox-${filter.name}-${option}`" />
              </div>
              <label :for="`mock-checkbox-${filter.name}-${option}`">{{ option }}</label>
            </div>
          </div>
          <CalChoiceToggle class="mt-4 sm:mt-0 mb-4" size="sm" on-label-class="text-sm" />
        </div>
      </div>
      <div class="flex xl:max-w-4xl 2xl:max-w-7xl flex-col w-full mb-3 xl:min-w-0">
        <BaseSection class="items-center text-center">
          <div class="flex w-full items-center flex-col md:flex-row gap-4 md:gap-0 pb-4">
            <div class="flex w-full flex-col space-y-6">
              <div v-for="tier in tiers" :key="tier.name" class="flex w-full flex-col">
                <div class="flex flex-row items-center mb-4">
                  <h2 class="text-2xl font-bold">{{ tier.name }} Plans</h2><span class="text-blue-600 ml-2">({{ tier.plans.length }} options)</span>
                </div>
                <div class="flex flex-row bg-gray-200 rounded-xl py-4 overflow-hidden">
                  <div v-for="(plan, index) in tier.plans" :key="index" class="flex-none w-full lg:w-1/2 max-w-[330px]">
                    <div class="flex flex-col mx-4 h-full rounded-md drop-shadow-md text-center bg-slate-50">
                      <div class="flex justify-center items-center">
                        <carrier-logo class="h-24 px-7 py-3 flex items-center" custom-class="max-w-full" :carrier-id="plan.carrierId" :coverage-type="applicationType" no-link="true" />
                      </div>
                      <div class="flex flex-grow items-center justify-center text-wrap pt-1 pb-5 text-blue font-semibold text-lg overflow-hidden h-22 px-10">
                        <p class="lining-nums">{{ plan.name }}<br />{{ plan.detail }}</p>
                      </div>
                      <div class="flex flex-col items-center object-cover bg-blue py-3 space-y-2">
                        <div class="h-6 w-32 rounded bg-white/30"></div>
                        <div class="h-3 w-40 rounded bg-white/20"></div>
                        <div class="h-3 w-40 rounded bg-white/20"></div>
                      </div>
                      <div class="flex flex-col space-y-2 content-center px-5 py-6">
                        <span>View Details</span>
                      </div>
                      <div class="content-end mb-5">
                        <span class="inline-block rounded-md border bg-red-600 px-6 py-2 text-white font-semibold">Select Plan</span>
                      </div>
                      <div class="flex justify-center items-center mb-4">
                        <label>Compare</label>
                        <div class="ml-2 w-5 h-5 border border-gray-300 rounded bg-white"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BaseSection>
      </div>
      <div class="hidden xl:flex flex-col xl:w-64 xl:pl-4 xl:shrink-0">
        <div class="flex w-full flex-row items-center justify-between">
          <span class="text-blue-500 text-lg font-semibold">FAQ’s</span>
          <hfc-icon icon="chevron-down" class="text-blue-500" />
        </div>
      </div>
    </div>
  </div>
</template>
